import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui';
import { Button } from '@/components/ui/button';
import { LogOut, Shield, AlertTriangle } from 'lucide-react';

export const SettingsPage: React.FC = () => {
  const [showConfirm, setShowConfirm] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('stratos_authenticated');
    window.location.reload();
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Paramètres</h1>
        <p className="text-gray-600 mt-2">Gérez votre compte et vos préférences</p>
      </div>

      {/* Security Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-stratos-gold" />
            <CardTitle>Sécurité</CardTitle>
          </div>
          <CardDescription>Gérez l'accès à votre plateforme</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <h3 className="font-semibold text-gray-900">Session active</h3>
              <p className="text-sm text-gray-600 mt-1">
                Vous êtes actuellement connecté à STRATOS
              </p>
            </div>
            <div className="flex items-center gap-2 text-green-600">
              <div className="w-2 h-2 bg-green-600 rounded-full animate-pulse" />
              <span className="text-sm font-medium">Connecté</span>
            </div>
          </div>

          <div className="pt-4 border-t border-gray-200">
            {!showConfirm ? (
              <Button
                variant="outline"
                onClick={() => setShowConfirm(true)}
                className="border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Se déconnecter
              </Button>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-3"
              >
                <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-red-900">Confirmer la déconnexion</h4>
                    <p className="text-sm text-red-700 mt-1">
                      Vous devrez saisir à nouveau votre code PIN pour accéder à la plateforme.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={handleLogout}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Confirmer la déconnexion
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowConfirm(false)}
                  >
                    Annuler
                  </Button>
                </div>
              </motion.div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* About Section */}
      <Card>
        <CardHeader>
          <CardTitle>À propos</CardTitle>
          <CardDescription>Informations sur la plateforme</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Version</p>
              <p className="font-semibold text-gray-900 mt-1">1.0.0</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Dernière mise à jour</p>
              <p className="font-semibold text-gray-900 mt-1">
                {new Date().toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </p>
            </div>
          </div>
          
          <div className="pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              <span className="font-semibold text-stratos-gold">STRATOS</span> - Consulting Premium F&B
            </p>
            <p className="text-sm text-gray-500 mt-1">
              © {new Date().getFullYear()} Tous droits réservés
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
